/**
 * Stałe zestawy sekcji kokpitu decyzji dla dokumentów Symfonii Handel („Grupuj według: Klasa /
 * kartoteka") — osobna sekcja na każdy rodzaj: „Sprzedaż" (`handel:sale`), „Magazyn"
 * (`handel:warehouse`) i „Zamówienia" (`handel:order`).
 *
 * Inaczej niż kartoteki z `section-kits.tsx`, dokumenty Handlu nie leżą w naszej bazie, więc zestaw jest
 * STATYCZNY: kolumny są zapisane tu na stałe (te same nazwy co na listach Handlu —
 * `tenant.handelMagazyn.columns.*`), bez katalogu pól, bez układu administratora i bez pól dodatkowych.
 * Wiersz sekcji to `HandelSectionRow` (`approvals-api.ts`); zapytanie nie niesie `?columns=`.
 *
 * Kolumny w kolejności ze zlecenia właściciela:
 *  - Sprzedaż — Numer (z kropką bufora), Status, Data wystawienia, Nabywca, Firma, Netto, Brutto, Typ;
 *  - Magazyn — Numer, Typ, Data wystawienia, Kontrahent, Firma, Netto, „Wartość";
 *  - Zamówienia — Numer, Typ, Data wystawienia, Kontrahent, Firma, Netto, Brutto.
 * Id kolumny = slug backendu (sort, filtr, suma „Razem"); wyjątek to „Firma" (id `company`, slug
 * `company_id` — jak na listach dokumentów). Sort domyślny: „Data wystawienia", od najnowszej.
 *
 * Filtry: tekst „zawiera", data i kwota „równe" to domyślne operatory wiersza filtrów. Okno filtra w
 * nagłówku ma tylko zakładkę „Warunek" (`conditionOnly`) — serwer nie ma podpowiedzi wartości dla
 * Handlu (`section/distinct` odpowiada 400 `DISTINCT_NOT_SUPPORTED`), więc panel o nie nie pyta.
 * Wyjątki, które nie potrzebują podpowiedzi: „Status" (stała lista „bufor / wystawiony") i „Firma"
 * (lista firm rekordów sekcji z serwera). Kolumna jest filtrowalna tylko wtedy, gdy serwer ją
 * zgłosił w `meta.filterable` (poza „Firmą", która ma kontrolkę zawsze — jej filtr po id firmy
 * serializuje się po `fieldSlug`, więc kolumna nie może go gubić).
 */

import type { ReactElement } from 'react';
import { HANDEL_TARGET_MODULE_CODE } from '@saas/shared';

import { Badge } from '@/components/Badge';
import type { ColumnDefinition, ColumnHeaderFilterConfig } from '@/components/configurable-table';
import { day } from '@/components/handel-magazyn/HmListShell';
import type {
  HandelGrossSectionRow,
  HandelSectionRowBase,
  HandelWarehouseSectionRow,
} from '@/lib/processes/approvals-api';
import { formatMoney } from '@/lib/processes/format-money';
import { cn } from '@/lib/utils';

import type {
  DecisionSectionRow,
  StaticSectionColumnsContext,
  StaticSectionKit,
} from './section-kits';

/**
 * Pusta komórka (brak wartości w Symfonii) — jeden zapis w całym zestawie: sam „—" bez stylu,
 * jak na listach Handlu (`day()` z `HmListShell` i komórki `?? '—'` w `HmSales`).
 */
const EMPTY = '—';

/** Remap id kolumny → slug sortu serwera: „Firma" sortuje po `company_id`, reszta po własnym id. */
const HANDEL_SORT_SLUG_OVERRIDES: Readonly<Record<string, string>> = { company: 'company_id' };

/** Komórka kwoty: kwota z kodem waluty wiersza (waluta jest zawsze); brak kwoty → „—". */
function MoneyValue({
  value,
  currencyCode,
}: {
  value: string | null;
  currencyCode: string;
}): ReactElement {
  if (value === null) return <>{EMPTY}</>;
  return <span className="tabular-nums text-text-primary">{formatMoney(value, currencyCode)}</span>;
}

/**
 * Filtr kolumny w wierszu filtrów (i w oknie nagłówka): tylko warunek (`conditionOnly` — bez
 * podpowiedzi wartości, których serwer dla Handlu nie ma), i tylko gdy serwer filtruje po tym slugu.
 * Kolumna bez filtra nie ma martwego pola „Szukaj…" (`filterable: false`).
 */
function filterOn(
  ctx: StaticSectionColumnsContext,
  category: 'text' | 'number' | 'date',
  slug: string,
): { filterable: false; headerFilter?: ColumnHeaderFilterConfig } {
  return {
    filterable: false,
    ...(ctx.isFilterable(slug)
      ? { headerFilter: { category, fieldSlug: slug, conditionOnly: true } }
      : {}),
  };
}

function documentNumberColumn<Row extends HandelSectionRowBase>(
  ctx: StaticSectionColumnsContext,
): ColumnDefinition<Row> {
  const { t } = ctx;
  const bufferLabel = t('tenant.handelMagazyn.status.buffer');
  const issuedLabel = t('tenant.handelMagazyn.status.issued');
  return {
    id: 'document_number',
    accessor: 'documentNumber',
    header: t('tenant.handelMagazyn.columns.documentNumber'),
    defaultVisible: true,
    defaultWidth: 170,
    sortable: ctx.isSortable('document_number'),
    ...filterOn(ctx, 'text', 'document_number'),
    renderCell: (_value, row) => {
      const dotLabel = row.inBuffer ? bufferLabel : issuedLabel;
      return (
        <span className="flex items-center gap-2">
          {/* Kropka bufora jak na listach Handlu: szara = bufor, zielona = wystawiony (brak = nieznany).
            Niesie ten sam tekst co plakietka „Status" (nazwa dostępna i podpowiedź): czytnik ekranu i
            najechanie myszą mówią to, co widać kolorem. */}
          {row.inBuffer !== null && (
            <span
              role="img"
              aria-label={dotLabel}
              title={dotLabel}
              className={cn(
                'h-2 w-2 shrink-0 rounded-full',
                row.inBuffer ? 'bg-text-tertiary' : 'bg-emerald-500',
              )}
              data-state={row.inBuffer ? 'buffer' : 'issued'}
              data-testid={`approvals-section-handel-dot-${row.id}`}
            />
          )}
          <span className="font-mono text-xs text-text-primary">{row.documentNumber ?? EMPTY}</span>
        </span>
      );
    },
  };
}

/** Status: serwer go nie przysyła — stan liczymy z `inBuffer` (`true` = bufor, `false` = wystawiony). */
function statusColumn<Row extends HandelSectionRowBase>(
  ctx: StaticSectionColumnsContext,
): ColumnDefinition<Row> {
  const { t } = ctx;
  return {
    id: 'status',
    accessor: (row) => row.inBuffer,
    header: t('tenant.handelMagazyn.columns.status'),
    defaultVisible: true,
    defaultWidth: 110,
    sortable: ctx.isSortable('status'),
    filterable: false,
    // Stała lista wyboru (tryb wartości `buffer` / `issued`) — opcje są w kodzie, więc zakładka
    // „Wartości" nie pyta serwera o podpowiedzi.
    ...(ctx.isFilterable('status')
      ? {
          headerFilter: {
            category: 'select' as const,
            fieldSlug: 'status',
            options: [
              { value: 'buffer', label: t('tenant.handelMagazyn.status.buffer') },
              { value: 'issued', label: t('tenant.handelMagazyn.status.issued') },
            ],
          },
        }
      : {}),
    renderCell: (_value, row) =>
      row.inBuffer === null ? (
        EMPTY
      ) : (
        <Badge
          tone={row.inBuffer ? 'neutral' : 'success'}
          size="sm"
          data-testid={`approvals-section-handel-status-${row.id}`}
        >
          {row.inBuffer
            ? t('tenant.handelMagazyn.status.buffer')
            : t('tenant.handelMagazyn.status.issued')}
        </Badge>
      ),
  };
}

function issueDateColumn<Row extends HandelSectionRowBase>(
  ctx: StaticSectionColumnsContext,
): ColumnDefinition<Row> {
  return {
    id: 'issue_date',
    accessor: 'issueDate',
    header: ctx.t('tenant.handelMagazyn.columns.issueDate'),
    defaultVisible: true,
    defaultWidth: 130,
    sortable: ctx.isSortable('issue_date'),
    ...filterOn(ctx, 'date', 'issue_date'),
    // Serwer zwraca sam dzień (`YYYY-MM-DD`) — pokazujemy go jak listy Handlu (ten sam `day()`).
    renderCell: (_value, row) => day(row.issueDate),
  };
}

/** Druga strona dokumentu: nabywca (sprzedaż) albo kontrahent (magazyn, zamówienia) — etykieta z `headerKey`. */
function contractorColumn<Row extends HandelSectionRowBase>(
  ctx: StaticSectionColumnsContext,
  headerKey: string,
): ColumnDefinition<Row> {
  return {
    id: 'contractor',
    accessor: 'contractorName',
    header: ctx.t(headerKey),
    defaultVisible: true,
    defaultWidth: 260,
    sortable: ctx.isSortable('contractor'),
    ...filterOn(ctx, 'text', 'contractor'),
    renderCell: (_value, row) => row.contractorName ?? EMPTY,
  };
}

/**
 * Firma rekordu: lista wyboru z firmami rekordów sekcji (z serwera, `companyOptions`), wartość = id
 * firmy. Kontrolka jest ZAWSZE (nawet przy pustej liście): filtr aktywny w stanie tabeli serializuje się
 * po `fieldSlug` kolumny, a kolumna bez `headerFilter` oddałaby serwerowi id kolumny (`company`).
 *
 * Komórka: nazwa firmy; firma usunięta — „(usunięto)", jak w sekcjach dokumentów (ten sam tekst
 * `ui.extRef.deletedTarget`): serwer odsyła wtedy id firmy, ale nie jej nazwę (`companyName: null`
 * przy ustawionym `companyId`); rekord bez firmy → „—". Lista opcji filtra nazywa firmę usuniętą jej
 * nazwą (`companyOptions`), żeby dało się ją wybrać.
 *
 * Ten sam tekst niesie `accessor` (nie surowe `companyName`): widoki „Karty" i Kanban tabeli czytają
 * wartość przez accessor i POMIJAJĄ pole puste, zanim w ogóle wywołają `renderCell` — z surowym
 * `companyName` firma usunięta znikałaby tam bez śladu zamiast pokazać „(usunięto)". Rekord bez firmy
 * daje `null` (pole pominięte w kartach, w komórce tabeli „—").
 */
function companyColumn<Row extends HandelSectionRowBase>(
  ctx: StaticSectionColumnsContext,
): ColumnDefinition<Row> {
  const deletedLabel = ctx.t('ui.extRef.deletedTarget');
  const labelOf = (row: Row): string | null =>
    row.companyName ?? (row.companyId !== null ? deletedLabel : null);
  return {
    id: 'company',
    accessor: labelOf,
    header: ctx.t('tenant.processes.approvals.columns.company'),
    defaultVisible: true,
    defaultWidth: 160,
    sortable: ctx.isSortable('company'),
    filterable: false,
    headerFilter: {
      category: 'record-select',
      fieldSlug: 'company_id',
      options: ctx.companies.map((company) => ({ value: company.id, label: company.name })),
    },
    renderCell: (_value, row) => labelOf(row) ?? EMPTY,
  };
}

function typeCodeColumn<Row extends HandelSectionRowBase>(
  ctx: StaticSectionColumnsContext,
): ColumnDefinition<Row> {
  return {
    id: 'type_code',
    accessor: 'typeCode',
    header: ctx.t('tenant.handelMagazyn.columns.typeCode'),
    defaultVisible: true,
    defaultWidth: 90,
    sortable: ctx.isSortable('type_code'),
    ...filterOn(ctx, 'text', 'type_code'),
    renderCell: (_value, row) => row.typeCode ?? EMPTY,
  };
}

/** Kolumna kwoty z sumą „Razem": id = slug backendu (sort, filtr „równe" i suma serwera). */
function amountColumn<Row extends HandelSectionRowBase>(
  ctx: StaticSectionColumnsContext,
  def: { id: string; headerKey: string; width: number; read: (row: Row) => string | null },
): ColumnDefinition<Row> {
  return {
    id: def.id,
    accessor: (row) => def.read(row),
    header: ctx.t(def.headerKey),
    defaultVisible: true,
    defaultWidth: def.width,
    sortable: ctx.isSortable(def.id),
    dataType: 'number',
    align: 'right',
    ...filterOn(ctx, 'number', def.id),
    renderCell: (_value, row) => (
      <MoneyValue value={def.read(row)} currencyCode={row.currencyCode} />
    ),
  };
}

const NET_HEADER_KEY = 'tenant.handelMagazyn.columns.net';

function netColumn<Row extends HandelSectionRowBase>(
  ctx: StaticSectionColumnsContext,
): ColumnDefinition<Row> {
  return amountColumn<Row>(ctx, {
    id: 'net_value',
    headerKey: NET_HEADER_KEY,
    width: 120,
    read: (row) => row.netValue,
  });
}

function grossColumn<Row extends HandelGrossSectionRow>(
  ctx: StaticSectionColumnsContext,
): ColumnDefinition<Row> {
  return amountColumn<Row>(ctx, {
    id: 'gross_value',
    headerKey: 'tenant.handelMagazyn.columns.gross',
    width: 130,
    read: (row) => row.grossValue,
  });
}

/** Składa stały zestaw sekcji z opisu kolumn typowanego wierszem Handlu (`Row`). */
function defineStaticSectionKit<Row extends HandelSectionRowBase>(definition: {
  sumSlugs: readonly string[];
  columns: (ctx: StaticSectionColumnsContext) => ColumnDefinition<Row>[];
}): StaticSectionKit {
  return {
    source: 'static',
    targetModuleCode: HANDEL_TARGET_MODULE_CODE,
    sortSlugOverrides: HANDEL_SORT_SLUG_OVERRIDES,
    // Ten sam porządek, który serwer stosuje bez `sort` — nagłówek „Data wystawienia" pokazuje go jawnie.
    defaultSort: { column: 'issue_date', direction: 'desc' },
    sumSlugs: definition.sumSlugs,
    // JEDYNY rzut typów w zestawach Handlu — granica rejestru (tak samo jak w `section-kits.tsx`):
    // kolumny są zbudowane na typie wiersza Handlu (`Row`), a rejestr trzyma je pod wspólnym typem
    // sekcji. Bezpieczne, bo tabela sekcji czyta z wiersza wyłącznie `id` i `decisions`, a resztę pól
    // czytają kolumny `Row`.
    buildColumns: (ctx) =>
      definition.columns(ctx) as unknown as ColumnDefinition<DecisionSectionRow>[],
  };
}

/** Sprzedaż — Numer, Status, Data wystawienia, Nabywca, Firma, Netto, Brutto, Typ. */
const HANDEL_SALE_KIT = defineStaticSectionKit<HandelGrossSectionRow>({
  sumSlugs: ['net_value', 'gross_value'],
  columns: (ctx) => [
    documentNumberColumn(ctx),
    statusColumn(ctx),
    issueDateColumn(ctx),
    contractorColumn(ctx, 'tenant.handelMagazyn.sales.columns.buyer'),
    companyColumn(ctx),
    netColumn(ctx),
    grossColumn(ctx),
    typeCodeColumn(ctx),
  ],
});

/** Magazyn — Numer, Typ, Data wystawienia, Kontrahent, Firma, Netto, „Wartość" (wartość dokumentu). */
const HANDEL_WAREHOUSE_KIT = defineStaticSectionKit<HandelWarehouseSectionRow>({
  sumSlugs: ['net_value', 'document_value'],
  columns: (ctx) => [
    documentNumberColumn(ctx),
    typeCodeColumn(ctx),
    issueDateColumn(ctx),
    contractorColumn(ctx, 'tenant.handelMagazyn.columns.contractor'),
    companyColumn(ctx),
    netColumn(ctx),
    amountColumn<HandelWarehouseSectionRow>(ctx, {
      id: 'document_value',
      headerKey: 'tenant.handelMagazyn.warehouse.columns.documentValue',
      width: 130,
      read: (row) => row.documentValue,
    }),
  ],
});

/** Zamówienia — Numer, Typ, Data wystawienia, Kontrahent, Firma, Netto, Brutto. */
const HANDEL_ORDER_KIT = defineStaticSectionKit<HandelGrossSectionRow>({
  sumSlugs: ['net_value', 'gross_value'],
  columns: (ctx) => [
    documentNumberColumn(ctx),
    typeCodeColumn(ctx),
    issueDateColumn(ctx),
    contractorColumn(ctx, 'tenant.handelMagazyn.columns.contractor'),
    companyColumn(ctx),
    netColumn(ctx),
    grossColumn(ctx),
  ],
});

/** Stałe zestawy sekcji wg klucza grupy z nagłówka koszyka (`handel:<rodzaj>`). */
export const HANDEL_SECTION_KITS: Readonly<Record<string, StaticSectionKit>> = {
  'handel:sale': HANDEL_SALE_KIT,
  'handel:warehouse': HANDEL_WAREHOUSE_KIT,
  'handel:order': HANDEL_ORDER_KIT,
};

/** Stały zestaw sekcji dla klucza grupy albo `undefined` (to nie jest rodzaj dokumentu Handlu). */
export function getStaticSectionKit(groupKey: string): StaticSectionKit | undefined {
  // `Object.hasOwn` — klucz grupy to dane z serwera; `in`/indeksowanie wprost trafiłoby
  // w odziedziczone nazwy z `Object.prototype`.
  return Object.hasOwn(HANDEL_SECTION_KITS, groupKey) ? HANDEL_SECTION_KITS[groupKey] : undefined;
}
